import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {FinancingFieldResponseBody} from '@dms/api/core';
import {CompositionPreviewResponseBody, FinancingCompositionLayer} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {getLayerName} from './getLayerName';

export type PreviewLayerWarning = {
  layerId: string;
  message: string;
};

/**
 * Turns the server render warnings into messages the user can act on. A per-layer issue names the
 * layer instead of its ID, and its English warning duplicate (which holds the layer ID) is dropped.
 */
export const getPreviewWarnings = (
  preview: Pick<CompositionPreviewResponseBody, 'warnings' | 'issues'>,
  layers: FinancingCompositionLayer[],
  fields: FinancingFieldResponseBody[]
) => {
  const layerWarnings = preview.issues
    .map((issue): PreviewLayerWarning | null => {
      const layer = layers.find((candidate) => candidate.id === issue.layerId);
      if (isNil(layer)) {
        return null;
      }
      const name = getLayerName(layer, fields);
      const message = match(issue.code)
        .with('TEXT_OVERFLOW_HEIGHT', () =>
          i18n.t('entity.watermark.composition.labels.textOverflowsHeight', {name})
        )
        .with('TEXT_OVERFLOW_WIDTH', () =>
          i18n.t('entity.watermark.composition.labels.textOverflowsWidth', {name})
        )
        .exhaustive();
      return {layerId: layer.id, message};
    })
    .filter(isNotNil);

  // Warnings about the whole render, e.g. that sample values are shown.
  const sceneWarnings = preview.warnings.filter(
    (warning) => !layerWarnings.some((layerWarning) => warning.includes(layerWarning.layerId))
  );

  return {layerWarnings, sceneWarnings};
};
